import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Printer, MessageCircle, Wallet, Smartphone } from 'lucide-react';

// Get the base URL from environment or use default
const getBaseUrl = () => {
  // Use the production URL if set, otherwise construct from window location
  if (process.env.REACT_APP_PRODUCTION_URL) {
    return process.env.REACT_APP_PRODUCTION_URL;
  }
  // In production, this should be your actual domain
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};

const BitsyQRCode = ({ 
  hotelName = "Hodler Inn",
  showPrintButton = true,
  variant = "full" // "full" or "compact"
}) => {
  const printRef = useRef(null);
  // Include source=qr_walkin to identify walk-in guests from lobby kiosk
  const bitsyUrl = `${getBaseUrl()}/#/book?source=qr_walkin`;

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');

    // Escape any HTML-special characters in dynamic values before interpolating
    // into the print template. `hotelName` comes from a prop today but defensive
    // escaping here protects against accidental future use of user-controlled
    // content (e.g. an admin-editable hotel display name).
    const escapeHtml = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const safeHotelName = escapeHtml(hotelName);

    printWindow.document.write(`
      <html>
        <head>
          <title>Bitsy QR Code - ${safeHotelName}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
              background: white;
            }
            .qr-card {
              text-align: center;
              padding: 40px;
              border: 3px solid #f59e0b;
              border-radius: 20px;
              max-width: 400px;
              background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
              color: white;
            }
            .hotel-name {
              font-size: 28px;
              font-weight: bold;
              margin-bottom: 8px;
              color: #f59e0b;
            }
            .tagline {
              font-size: 14px;
              color: #9ca3af;
              margin-bottom: 24px;
            }
            .qr-container {
              background: white;
              padding: 20px;
              border-radius: 16px;
              display: inline-block;
              margin: 20px 0;
            }
            .assistant-name {
              font-size: 24px;
              font-weight: bold;
              color: white;
              margin-top: 20px;
            }
            .assistant-desc {
              font-size: 14px;
              color: #d1d5db;
              margin-bottom: 20px;
            }
            .features {
              display: flex;
              flex-wrap: wrap;
              justify-content: center;
              gap: 12px;
              margin-top: 20px;
            }
            .feature {
              background: rgba(245, 158, 11, 0.2);
              border: 1px solid rgba(245, 158, 11, 0.3);
              padding: 8px 16px;
              border-radius: 20px;
              font-size: 12px;
              color: #fbbf24;
            }
            .scan-text {
              font-size: 18px;
              font-weight: 600;
              color: #f59e0b;
              margin-top: 24px;
            }
            @media print {
              body { background: white; }
              .qr-card { box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="qr-card">
            <div class="hotel-name">${safeHotelName}</div>
            <div class="tagline">Be Responsible to Be Free</div>
            <div class="qr-container">
              ${printContent.querySelector('.qr-code-svg').outerHTML}
            </div>
            <div class="assistant-name">Meet Bitsy</div>
            <div class="assistant-desc">Your AI Booking Assistant</div>
            <div class="features">
              <div class="feature">📱 Book a Room</div>
              <div class="feature">💳 Pay with Crypto</div>
              <div class="feature">🔊 Voice Enabled</div>
            </div>
            <div class="scan-text">Scan to Book & Pay</div>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const handleDownload = () => {
    const svg = printRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const a = document.createElement('a');
      a.download = 'bitsy-qr-code.png';
      a.href = canvas.toDataURL('image/png');
      a.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (variant === "compact") {
    return (
      <div className="text-center p-4">
        <div ref={printRef}>
          <div className="qr-code-svg">
            <QRCodeSVG 
              value={bitsyUrl}
              size={120}
              level="H"
              includeMargin={true}
              bgColor="#ffffff"
              fgColor="#000000"
            />
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Scan to book with Bitsy</p>
      </div>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-amber-500/30 overflow-hidden">
      <CardHeader className="border-b border-amber-500/20 bg-amber-500/10">
        <CardTitle className="text-amber-400 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          Bitsy QR Code for Walk-In Guests
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          {/* QR Code */}
          <div ref={printRef} className="bg-white p-4 rounded-xl shadow-lg">
            <div className="qr-code-svg">
              <QRCodeSVG 
                value={bitsyUrl}
                size={180}
                level="H"
                includeMargin={true}
                bgColor="#ffffff"
                fgColor="#000000"
              />
            </div>
          </div>
          
          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-white text-xl font-bold mb-2">Self-Service Booking</h3>
            <p className="text-gray-400 text-sm mb-4">
              Place this QR code at your front desk or lobby. Walk-in guests can scan to:
            </p>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
              <span className="bg-amber-500/20 text-amber-400 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Book a Room
              </span>
              <span className="bg-amber-500/20 text-amber-400 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Pay with Crypto
              </span>
              <span className="bg-amber-500/20 text-amber-400 text-xs px-3 py-1 rounded-full flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> Chat with Bitsy
              </span>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-3 mb-4">
              <p className="text-gray-400 text-xs mb-1">QR Code URL:</p>
              <code className="text-amber-400 text-xs break-all">{bitsyUrl}</code>
            </div>
            
            {showPrintButton && (
              <div className="flex gap-2 justify-center md:justify-start">
                <Button
                  onClick={handlePrint}
                  className="bg-amber-500 hover:bg-amber-400 text-black"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print QR Card
                </Button>
                <Button
                  onClick={handleDownload}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BitsyQRCode;
